/**
 * Checkbox — React (web) implementation.
 *
 * All styling references the shared token constants in Checkbox.ts.
 * No raw hex values or pixel literals in this file.
 */

import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { Icon } from '../Icon/Icon.web';
import { Text } from '../Text/Text.web';
import {
  CHECKBOX_SIZE,
  CHECKBOX_BORDER_WIDTH,
  CHECKBOX_RADIUS,
  CHECKBOX_TICK_ICON,
  CHECKBOX_TICK_SIZE,
  CHECKBOX_TICK_COLOR,
  CHECKBOX_LABEL_GAP,
  CHECKBOX_LABELLED_MIN_HEIGHT,
  CHECKBOX_LABEL_VARIANT,
  CHECKBOX_LABEL_COLOR,
  checkboxBoxColorsFor,
} from './Checkbox';

export interface WebCheckboxProps {
  actionId?: string;
  checked?: boolean;
  disabled?: boolean;
  accessibilityLabel: string;
  /** Visible text beside the box; clicking it toggles the box. */
  label?: string;
  /** Alert border, e.g. a required tick left empty. */
  error?: boolean;
  onChange?: (next: boolean) => void;
  'data-testid'?: string;
}

const CHECKBOX_FOCUS_STYLE_ID = 'galapagos-checkbox-focus-visible';
const CHECKBOX_FOCUS_SELECTOR = '[data-galapagos-checkbox="true"]';

export function Checkbox({
  actionId = 'ui.checkbox.change',
  checked = false,
  disabled = false,
  accessibilityLabel,
  label,
  error = false,
  onChange,
  'data-testid': dataTestId,
}: WebCheckboxProps) {
  ensureFocusVisibleStyle(CHECKBOX_FOCUS_STYLE_ID, [CHECKBOX_FOCUS_SELECTOR]);

  const boxColors = checkboxBoxColorsFor(checked, error);
  const boxStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    // Intrinsic size floor: a flex row with a long sibling label must never
    // squeeze the box (ADR-020 Rule 2 keeps this to min size, not root flex).
    minWidth: CHECKBOX_SIZE,
    minHeight: CHECKBOX_SIZE,
    borderRadius: CHECKBOX_RADIUS,
    borderWidth: CHECKBOX_BORDER_WIDTH,
    borderStyle: 'solid',
    borderColor: boxColors.border,
    backgroundColor: boxColors.background,
    padding: 0,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
  // With a label the button is a row: the text sits inside the one checkbox
  // control, so clicking it toggles and it is not announced a second time.
  const rowStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: CHECKBOX_LABEL_GAP,
    minHeight: CHECKBOX_LABELLED_MIN_HEIGHT,
    minWidth: CHECKBOX_LABELLED_MIN_HEIGHT,
    padding: 0,
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
  const tick = checked ? (
    <Icon
      name={CHECKBOX_TICK_ICON}
      size={CHECKBOX_TICK_SIZE}
      color={CHECKBOX_TICK_COLOR}
      aria-hidden
    />
  ) : null;

  return (
    <button
      data-analytics-action={actionId}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-invalid={error || undefined}
      aria-label={accessibilityLabel}
      // Stay focusable when disabled (ADR-025): expose aria-disabled and no-op
      // the handler rather than setting the native `disabled` attribute, which
      // would drop the control from keyboard/screen-reader navigation.
      onClick={disabled ? undefined : () => onChange?.(!checked)}
      data-galapagos-checkbox="true"
      data-testid={dataTestId}
      style={label === undefined ? boxStyle : rowStyle}
    >
      {label === undefined ? (
        tick
      ) : (
        <>
          <span aria-hidden style={boxStyle}>
            {tick}
          </span>
          <span aria-hidden>
            <Text variant={CHECKBOX_LABEL_VARIANT} color={CHECKBOX_LABEL_COLOR} value={label} />
          </span>
        </>
      )}
    </button>
  );
}
